import React, { useRef, useState, useEffect } from 'react';
import { toPng } from 'html-to-image';
import axios from 'axios';
import TopArtists from './components/TopArtist';
import TopAlbums from './components/TopAlbums';
import TopTracks from './components/TopTracks';
import RecentTracks from './components/RecentTracks';
import UserInfo from './components/UserInfo';
import RecentTracksList from './components/RecentTracksList';
import ImgArtists from './components/ImgArtist';
import ImgTracks from './components/ImgTrack';
import ScreenSaver from './components/ScreenSaver';

import './App.css'

const API_KEY = 'fcdd229d722f1657cd7fedb044df92d2';
const BASE_URL = 'https://ws.audioscrobbler.com/2.0/';

const fetchAllData = async (username, period) => {
  const [userRes, recentRes, albumRes, artistRes, trackRes] = await Promise.all([
    axios.get(BASE_URL, { params: { method: 'user.getInfo', user: username, api_key: API_KEY, format: 'json' } }),
    axios.get(BASE_URL, { params: { method: 'user.getRecentTracks', user: username, api_key: API_KEY, format: 'json', limit: 25 } }),
    axios.get(BASE_URL, { params: { method: 'user.getTopAlbums', user: username, period, api_key: API_KEY, format: 'json' } }),
    axios.get(BASE_URL, { params: { method: 'user.getTopArtists', user: username, period, api_key: API_KEY, format: 'json' } }),
    axios.get(BASE_URL, { params: { method: 'user.getTopTracks', user: username, period, api_key: API_KEY, format: 'json' } }),
  ]);
  return {
    userInfo: userRes.data.user,
    recentTracks: recentRes.data.recenttracks.track,
    topAlbums: albumRes.data.topalbums.album,
    topArtists: artistRes.data.topartists.artist,
    topTracks: trackRes.data.toptracks.track,
  };
};

function App() {
  const [username, setUsername] = useState('');
  const [period, setPeriod] = useState('7day');
  const [submittedUsername, setSubmittedUsername] = useState('');
  const [submittedPeriod, setSubmittedPeriod] = useState('');
  const [fetchedData, setFetchedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showScreenSaver, setShowScreenSaver] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'));

  const handleOpenScreenSaver = () => {
    setShowScreenSaver(true);
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch((e) => console.log(e));
    }
  };

  const handleCloseScreenSaver = () => {
    setShowScreenSaver(false);
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch((e) => console.log(e));
    }
  };

  const periodMap = {
    '7day': 'Weekly',
    '1month': 'Monthly',
    '12month': 'Yearly',
    'overall': 'Lifetime'
  };

  const [fetchError, setFetchError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username.trim()) return;
    setIsLoading(true);
    setFetchedData(null);
    setFetchError(null);
    try {
      const result = await fetchAllData(username, period);
      setFetchedData(result);
      setSubmittedUsername(username);
      setSubmittedPeriod(period);
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError('could not load data — check the username and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const elementRef = useRef();

  const fallbackImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';

  const handleDownloadImage = () => {
    toPng(elementRef.current, {
      cacheBust: true,
      skipFonts: true,
    })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `mezan-${submittedUsername || 'export'}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Error converting DOM to image:', err);
      });
  };

  return (
    <div className="main-page">
      <div className="img-ren">
        <div className="biggercont">
          <div ref={elementRef} className="containe">
            {submittedUsername && (
              <div className="poster-header">
                <h1>{periodMap[submittedPeriod] || 'Weekly'} summary</h1>
                <p>Top tracks & artists for {submittedUsername}</p>
              </div>
            )}
            <div className="lastfm-char">
              {fetchedData && (
                <div className="image-boar">
                  {fetchedData.topAlbums.slice(0, 25).map((item, index) => (
                    <div className="img" key={index}>
                      <div className="image">
                        <img
                          src={item.image[3]['#text'] || fallbackImage}
                          alt={item.name}
                          crossOrigin="anonymous"
                          onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="top">
              <div className="toptrack">
                {fetchedData && <ImgTracks tracks={fetchedData.topTracks} />}
              </div>
              <div className="topart">
                {fetchedData && <ImgArtists artists={fetchedData.topArtists} />}
              </div>
            </div>
            <div className="usercardd">
              {fetchedData && <UserInfo userInfo={fetchedData.userInfo} />}
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard">
        <nav className="nav">
          <span className="logo">mezan.</span>
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="last.fm username"
            />
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="7day">weekly</option>
              <option value="1month">monthly</option>
              <option value="12month">yearly</option>
              <option value="overall">lifetime</option>
            </select>
            <div className="nav-divider" />
            <button type="submit">fetch</button>
            <button type="button" onClick={handleOpenScreenSaver}>screensaver</button>
            <button className="download-button" type="button" onClick={handleDownloadImage}>download</button>
            <div className="nav-divider" />
            <button className="theme-toggle" type="button" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'light' ? '○ dark' : '● light'}
            </button>
          </form>
        </nav>

        {isLoading && (
          <div className="loader-overlay">
            <span className="loader-text">fetching<span className="loader-dots" /></span>
          </div>
        )}

        {!isLoading && fetchError && (
          <div className="loader-overlay">
            <span className="loader-text" style={{ color: 'var(--accent)' }}>{fetchError}</span>
          </div>
        )}

        {!isLoading && !fetchError && !submittedUsername && (
          <div className="loader-overlay">
            <span className="loader-text">enter a last.fm username to begin</span>
          </div>
        )}

        {!isLoading && !fetchError && fetchedData && (
          <div className="app">
            <div className="sidebar">
              <div className="sidebar-user">
                <div className="usercard">
                  <div className="section-label"><span className="num">01</span> Listener</div>
                  <UserInfo userInfo={fetchedData.userInfo} />
                </div>
                <div className="playing-history">
                  <div className="section-label"><span className="num">02</span> In Rotation</div>
                  <RecentTracks track={fetchedData.recentTracks[0]} />
                </div>
              </div>
              <div>
                <div className="section-label"><span className="num">03</span> Selected Works</div>
                <div className="lastfm-chart">
                  <div className="image-board">
                    {fetchedData.topAlbums.slice(0, 25).map((item, index) => (
                      <div className="img" key={index}>
                        <div className="images">
                          <img
                            src={item.image[3]['#text'] || fallbackImage}
                            alt={item.name}
                            crossOrigin="anonymous"
                            onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="lastfm">
              <div className="info">
                <RecentTracksList tracks={fetchedData.recentTracks} />
              </div>
              <div className="tops">
                <TopAlbums albums={fetchedData.topAlbums} />
                <TopArtists artists={fetchedData.topArtists} />
                <TopTracks tracks={fetchedData.topTracks} />
              </div>
            </div>
          </div>
        )}
      </div>

      {showScreenSaver && submittedUsername && (
        <ScreenSaver username={submittedUsername} onClose={handleCloseScreenSaver} />
      )}

      {showScreenSaver && !submittedUsername && username && (
        <ScreenSaver username={username} onClose={handleCloseScreenSaver} />
      )}
    </div>
  );
}

export default App;
